import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
    Card, Typography, Accordion, AccordionSummary, AccordionDetails, Box, Button,
    IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Chip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import axios from "axios";
import { getAuth } from "firebase/auth";
import { Link } from "react-router-dom";

// Helper function to determine if a venue needs renewal highlighting
const getVenueStatus = (venue) => {
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);

    const upcomingSessions = venue.sessions?.filter(
        (session) => new Date(session.eventEndTime) > now
    );

    if (!upcomingSessions || upcomingSessions.length === 0) {
        return 'no-upcoming';
    }

    // --- REFINED LOGIC: Sort sessions to reliably find the last one ---
    const sortedSessions = upcomingSessions.sort((a, b) =>
        new Date(a.eventEndTime) - new Date(b.eventEndTime)
    );
    const lastSession = sortedSessions[sortedSessions.length - 1];
    // --- End of Refined Logic ---

    if (new Date(lastSession.eventEndTime) < thirtyDaysFromNow) {
        return 'needs-renewal';
    }

    return 'safe';
};


const MyVenuesComponent = () => {
    const [venues, setVenues] = useState([]);
    const [editingVenueId, setEditingVenueId] = useState(null);
    const [editedVenueName, setEditedVenueName] = useState("");

    // State for the Re-book Dialog
    const [rebookDialogOpen, setRebookDialogOpen] = useState(false);
    const [sessionToRebook, setSessionToRebook] = useState(null);
    const [rebookDays, setRebookDays] = useState(30);
    const [isLoading, setIsLoading] = useState(false);

    const fetchVenues = useCallback(async () => {
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) return;

            const response = await axios.get(`/api/venues/${user.uid}`);
            setVenues(response.data);
        } catch (error) {
            console.error("Error fetching venues:", error);
        }
    }, []);

    useEffect(() => {
        fetchVenues();
    }, [fetchVenues]);

    const processedVenues = useMemo(() => {
        if (!venues) return [];
        return venues
            .map(venue => ({
                ...venue,
                status: getVenueStatus(venue),
                hasUpcoming: venue.sessions?.some(s => new Date(s.eventEndTime) > new Date()),
            }))
            .filter(venue => venue.hasUpcoming)
            .sort((a, b) => {
                if (a.status === 'needs-renewal' && b.status !== 'needs-renewal') return -1;
                if (a.status !== 'needs-renewal' && b.status === 'needs-renewal') return 1;
                return (a.venueName || '').localeCompare(b.venueName || '');
            });
    }, [venues]);

    const handleOpenRebookDialog = (session) => {
        setSessionToRebook(session);
        setRebookDialogOpen(true);
    };

    const handleCloseRebookDialog = () => {
        setRebookDialogOpen(false);
        setSessionToRebook(null);
        setRebookDays(30);
    };

    const handleDuplicateSession = async () => {
        if (!sessionToRebook || !rebookDays) return;
        setIsLoading(true);

        const newSessionPayload = { ...sessionToRebook };
        delete newSessionPayload._id;
        delete newSessionPayload.createdAt;
        delete newSessionPayload.updatedAt;
        delete newSessionPayload.__v;

        // --- 1. Calculate New Dates and Times ---
        const originalStartDate = new Date(sessionToRebook.eventStartTime);
        const originalEndDate = new Date(sessionToRebook.eventEndTime);
        const daysToAdd = parseInt(rebookDays, 10);

        // Increment both dates/times
        originalStartDate.setDate(originalStartDate.getDate() + daysToAdd);
        originalEndDate.setDate(originalEndDate.getDate() + daysToAdd);

        const newEventStartTimeISO = originalStartDate.toISOString();
        const newEventEndTimeISO = originalEndDate.toISOString();

        // CRITICAL FIX 1: Set all three date/time fields consistently and correctly
        newSessionPayload.eventDate = newEventStartTimeISO;
        newSessionPayload.eventStartTime = newEventStartTimeISO;
        newSessionPayload.eventEndTime = newEventEndTimeISO;

        // --- 2. Reset Status and User/Staff Data for a New Session ---
        // CRITICAL FIX 2: Force status to UPCOMING and reset history
        newSessionPayload.currentStatus = "UPCOMING";
        newSessionPayload.statusHistory = [{ status: "UPCOMING", timestamp: new Date() }];

        newSessionPayload.reservedBy = [];
        newSessionPayload.attendees = [];
        newSessionPayload.coachFirebaseUid = [];
        newSessionPayload.siteManagerFirebaseUid = [];
        newSessionPayload.coachStaff = [];
        newSessionPayload.siteManagerStaff = [];
        newSessionPayload.acceptedBy = { coaches: [], siteManagers: [] };
        newSessionPayload.coachOffer = [];
        newSessionPayload.siteManagerOffer = [];
        newSessionPayload.reservations = 0;

        // --- 3. Image Duplication Fix (for carousel visibility) ---
        if (sessionToRebook.venueImage1) {
            newSessionPayload.venueImage1 = sessionToRebook.venueImage1;
            newSessionPayload.venueImage2 = sessionToRebook.venueImage2 || sessionToRebook.venueImage1;
            newSessionPayload.venueImage3 = sessionToRebook.venueImage3 || sessionToRebook.venueImage1;
        }
        // ------------------------------------------------------------------

        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) throw new Error("User not authenticated.");

            const token = await user.getIdToken();
            const apiPayload = { sessions: [newSessionPayload] };

            const response = await axios.post(
                "https://api.admin.academy.marsreel.com/api/sessions",
                apiPayload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const newlyCreatedSession = response.data[0];

            setVenues(currentVenues => {
                const updatedVenues = JSON.parse(JSON.stringify(currentVenues));
                const venueIndex = updatedVenues.findIndex(
                    venue => venue.sessions.some(s => s._id === sessionToRebook._id)
                );

                if (venueIndex > -1) {
                    updatedVenues[venueIndex].sessions.push(newlyCreatedSession);
                }

                return updatedVenues;
            });

            alert("Session successfully duplicated!");
            handleCloseRebookDialog();

        } catch (error) {
            console.error("Error duplicating session:", error);
            alert(`Failed to duplicate session: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsLoading(false);
        }
    };
    const handleEditClick = (venue) => { setEditingVenueId(venue._id); setEditedVenueName(venue.venueName); };
    const handleSaveClick = async (venueId) => {
        try {
            await axios.put(`/api/active-locations/${venueId}/venue-name`, { venueName: editedVenueName });
            setVenues((prevVenues) =>
                prevVenues.map((venue) =>
                    venue._id === venueId ? { ...venue, venueName: editedVenueName } : venue
                )
            );
            setEditingVenueId(null);
        } catch (error) {
            console.error("Error updating venue name:", error);
        }
    };
    const handleCancelClick = () => { setEditingVenueId(null); setEditedVenueName(""); };
    const formatDateTime = (utcString) => new Date(utcString).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

    const futureDateString = useMemo(() => {
        if (!sessionToRebook || !rebookDays || rebookDays <= 0) {
            return null;
        }

        const startDate = new Date(sessionToRebook.eventStartTime);
        const endDate = new Date(sessionToRebook.eventEndTime);
        const daysToAdd = parseInt(rebookDays, 10);

        startDate.setDate(startDate.getDate() + daysToAdd);
        endDate.setDate(endDate.getDate() + daysToAdd);

        const dateOptions = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
        const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };

        const formattedDate = startDate.toLocaleDateString(undefined, dateOptions);
        const formattedStartTime = startDate.toLocaleTimeString(undefined, timeOptions);
        const formattedEndTime = endDate.toLocaleTimeString(undefined, timeOptions);

        return `${formattedDate}, from ${formattedStartTime} to ${formattedEndTime}`;
    }, [sessionToRebook, rebookDays]);


    return (
        <>
            <Card
                sx={{
                    backgroundColor: "#202020",
                    color: "#fff",
                    padding: "1.5rem",
                    borderRadius: "12px",
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
                }}
            >
                <Typography
                    variant="h6"
                    sx={{ color: "#fff", fontWeight: "bold", marginBottom: "1rem" }}
                >
                    My Venues
                </Typography>

                {processedVenues.map((venue) => (
                    <Accordion
                        key={venue._id}
                        sx={{
                            color: "#fff",
                            marginBottom: "8px",
                            borderRadius: "8px",
                            backgroundColor: venue.status === 'needs-renewal' ? '#5A2D2D' : '#282828',
                            border: venue.status === 'needs-renewal' ? '1px solid #C94C4C' : 'none',
                            transition: 'background-color 0.3s ease',
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#12c099" }} />}>
                            {editingVenueId === venue._id ? (
                                <Box sx={{ display: "flex", alignItems: "center", width: '100%' }}>
                                    <TextField value={editedVenueName} onChange={(e) => setEditedVenueName(e.target.value)} size="small" variant="outlined" sx={{ backgroundColor: "#fff", borderRadius: "4px", marginRight: "8px", flexGrow: 1, }} />
                                    <IconButton onClick={() => handleSaveClick(venue._id)} color="success"><CheckIcon /></IconButton>
                                    <IconButton onClick={handleCancelClick} color="error"><CloseIcon /></IconButton>
                                </Box>
                            ) : (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Button component={Link} to={`/facilities/${venue._id ? venue._id.toString() : "undefined"}`} sx={{ fontSize: "1rem", fontWeight: "bold", textTransform: "none", color: "#12c099", }}>
                                        {venue.venueName}
                                    </Button>
                                    <IconButton onClick={(e) => { e.stopPropagation(); handleEditClick(venue); }} sx={{ color: "#12c099", marginLeft: "8px" }}><EditIcon /></IconButton>
                                </Box>
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                {venue.sessions.filter(session => new Date(session.eventEndTime) > new Date()).map((session, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                                        {/********** CHANGE: Button is now a Link **********/}
                                        <Button
                                            variant="outlined"
                                            component={Link}
                                            to={`/session/${session._id}`}
                                            sx={{
                                                borderColor: "#12c099",
                                                color: "#12c099",
                                                fontSize: "0.85rem",
                                                fontWeight: "bold",
                                                padding: "6px 12px",
                                                borderRadius: "6px",
                                            }}
                                        >
                                            {`${formatDateTime(session.eventStartTime)} - ${formatDateTime(session.eventEndTime)}`}
                                        </Button>
                                        {/********** END OF CHANGE **********/}
                                        <IconButton size="small" onClick={() => handleOpenRebookDialog(session)} sx={{ color: "#12c099", marginLeft: '4px' }} title="Re-book this session">
                                            <AddCircleOutlineIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Card>

            <Dialog open={rebookDialogOpen} onClose={handleCloseRebookDialog} PaperProps={{ sx: { backgroundColor: '#282828', color: '#fff', width: '500px' } }}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Re-book Session</DialogTitle>
                <DialogContent>
                    <Typography>
                        Duplicate "{sessionToRebook?.name}" for a future date.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, my: 2 }}>
                        {[14, 30, 60].map(days => (
                            <Chip
                                key={days}
                                label={`${days} Days`}
                                onClick={() => setRebookDays(days)}
                                color={rebookDays === days ? 'primary' : 'default'}
                                sx={{ cursor: 'pointer', backgroundColor: rebookDays === days ? '#12c099' : '#555', color: 'white' }}
                            />
                        ))}
                    </Box>
                    <TextField
                        fullWidth
                        label="Or enter custom days"
                        type="number"
                        value={rebookDays}
                        onChange={(e) => setRebookDays(Number(e.target.value))}
                        variant="outlined"
                        InputLabelProps={{ sx: { color: '#aaa' } }}
                        sx={{ input: { color: '#fff' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#555' } } }}
                    />
                    {futureDateString && (
                        <Box sx={{ mt: 2.5, p: 1.5, backgroundColor: 'rgba(255, 255, 255, 0.08)', borderRadius: 1, border: '1px solid #444' }}>
                            <Typography sx={{ fontWeight: 'bold', color: '#12c099', fontSize: '0.9rem' }}>
                                NEW SESSION DATE:
                            </Typography>
                            <Typography>
                                {futureDateString}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px' }}>
                    <Button onClick={handleCloseRebookDialog} sx={{ color: '#aaa' }}>Cancel</Button>
                    <Button
                        onClick={handleDuplicateSession}
                        disabled={isLoading || !rebookDays || rebookDays <= 0}
                        variant="contained"
                        sx={{ backgroundColor: '#12c099', '&:hover': { backgroundColor: '#0fa383' } }}
                    >
                        {isLoading ? "Duplicating..." : "Confirm Re-book"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MyVenuesComponent;
